<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import InfoIcon from '../../icons/InfoIcon.vue';
import OkayIcon from '../../icons/OkayIcon.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import Button from '@/dashboard/vue/components/common/Button.vue';
import { usePurchaseStore } from '@/dashboard/store/purchaseStore';
import DropdownArrowIcon from '@/dashboard/vue/components/icons/DropdownArrowIcon.vue';
import PaymentPromoInput from '@/dashboard/vue/components/popups/PaymentPopup/PaymentPromoInput.vue';
import CloseCircleIcon from '@/dashboard/vue/components/icons/CloseCircleIcon.vue';
import Tooltip from '@/dashboard/vue/components/common/Tooltip.vue';
import { UserRole } from '@/dashboard/types/user_role';

const purchaseStore = usePurchaseStore();
const { period, transaction, transactionUsers, discountCode, paymentError, discountStatus, discount } = storeToRefs(purchaseStore);
const label = computed(() => (period.value === 'year' ? 'annually' : 'monthly'));
const showDiscountField = ref(false);

function toggleDiscountField() {
  if (!discount.value) {
    showDiscountField.value = !showDiscountField.value;
  } else {
    showDiscountField.value = true;
  }
}
</script>

<template>
  <div class="relative flex w-[1080px] justify-center text-black dark:text-white">
    <div v-if="paymentError" class="text-caption-s absolute top-s flex w-[463px] items-start rounded-6 bg-[#FE5859]/10 text-mid-red">
      <span class="p-[6px]">
        <CloseCircleIcon class="stroke-mid-red" />
      </span>
      <span class="py-[3px] pr-[6px]">{{ paymentError }}</span>
    </div>

    <div class="flex max-w-[608px] flex-1 flex-col pt-[88px]">
      <div class="mb-[102px] flex flex-col gap-[6px]">
        <div>
          <div class="flex items-center justify-between dark:text-gray-500">
            <span class="text-caption-s">Type:</span>

            <div class="flex items-center">
              <span class="text-caption-s capitalize">{{ label }}</span>
            </div>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-750 mt-[15px]"></Divider>
        </div>

        <div v-for="item in transactionUsers" :key="item.role" class="relative">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-xs">
              <span class="text-caption-s">{{ item.count }} {{ item.role }}(s)</span>
              <Tooltip>
                <template #title>
                  <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                </template>
                <template #body>
                  <span v-if="item.role === UserRole.Owner" class="w-[264px]">
                    Owner can fully manage the workspace and use all the features of teampaper.
                    <br /><br />
                    <a href="https://somebay.com/teampaper/roles" target="_blank" class="underline underline-offset-2">More info about roles ↗</a>
                  </span>
                  <span v-else-if="item.role === UserRole.Admin" class="w-[264px]">
                    Admins can use all the features of teampaper and fully manage the workspace, but:
                    <ul class="list-inside list-disc">
                      <li>Cannot delete the workspace;</li>
                      <li>Cannot manage billing.</li>
                    </ul>
                    <br />
                    <a href="https://somebay.com/teampaper/roles" target="_blank" class="underline underline-offset-2">More info about roles ↗</a>
                  </span>
                  <span v-else-if="item.role === UserRole.Creator" class="w-[264px]">
                    <ul class="list-inside list-disc">
                      <li>Creator has the ability to use all the features of teampaper as well as some administrative capabilities.</li>
                      <li>He won't be invited to the Shared with the team directories already made (a separate invitation is needed)</li>
                    </ul>
                    <br />
                    <a href="https://somebay.com/teampaper/roles" target="_blank" class="underline underline-offset-2">More info about roles ↗</a>
                  </span>
                  <span v-else-if="item.role === UserRole.Viewer" class="w-[264px]">
                    <ul class="list-inside list-disc">
                      <li>Viewers can only watch & comment but can be invited to a workspace.</li>
                      <li>Client can only watch & comment but can only be invited to a directory.</li>
                      <li>Both roles won't be invited to the team directories already made (a separate invitation is needed)</li>
                    </ul>
                    <br />
                    <a href="https://somebay.com/teampaper/roles" target="_blank" class="underline underline-offset-2">More info about roles ↗</a>
                  </span>
                </template>
              </Tooltip>
            </div>

            <div class="flex items-center gap-xs">
              <span class="text-caption-s">{{ item.currency_code }} {{ item.price }}</span>
            </div>
          </div>

          <Divider class="divider-gray-450 dark:divider-gray-750 mt-xs"></Divider>
        </div>

        <div>
          <div class="mt-xs flex items-start justify-between">
            <div class="flex flex-col gap-xs">
              <button class="flex items-center gap-x-xs text-brand-night-blue" @click="toggleDiscountField">
                <span class="text-caption-s"> I have a discount </span>
                <DropdownArrowIcon v-if="!discount" class="stroke-brand-night-blue" :class="{ 'rotate-180': showDiscountField }" />
              </button>

              <PaymentPromoInput
                v-if="showDiscountField"
                :code="discountCode"
                :status="discountStatus"
                :on-change="purchaseStore.changeDiscountCode"
                :on-add-click="purchaseStore.getDiscount"
              />
            </div>

            <div class="flex flex-col items-end">
              <div v-if="transaction?.details.credit" class="flex items-center gap-xs">
                <Tooltip>
                  <template #title>
                    <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                  </template>
                  <template #body>
                    <span class="w-[264px]">The sum of subscribed seat(s) (of type Owner, Admin, or Creator).</span>
                  </template>
                </Tooltip>
                <span class="text-caption-s">
                  <span>
                    Prepaid seat(s): {{ +(transaction?.details.credit ?? 0) > 0 ? '-' : '' }}{{ transaction?.details.currency_code }}
                    {{ +(transaction?.details.credit ?? 0) / 100 }}
                  </span>
                </span>
              </div>
              <div v-if="transaction?.details.credit" class="flex items-center gap-xs">
                <span class="text-caption-s">
                  <span>
                    Discount: {{ +(transaction?.details.discount ?? 0) > 0 ? '-' : '' }}{{ transaction?.details.currency_code }}
                    {{ +(transaction?.details.discount ?? 0) / 100 }}
                  </span>
                </span>
              </div>
              <div class="flex items-center gap-xs">
                <span class="text-caption-s">
                  <span>VAT: {{ transaction?.details.currency_code }} {{ +(transaction?.details.tax ?? 0) / 100 }}</span>
                </span>
              </div>
              <div class="flex items-center gap-xs">
                <span class="text-caption-s">
                  <span>Total: {{ transaction?.details.currency_code }} {{ +(transaction?.details.grand_total ?? 0) / 100 }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-m flex flex-col items-center">
        <div class="flex items-center gap-xs">
          <OkayIcon class="fill-green" />
          <span class="text-caption-s text-green">If you forgot someone, don't worry; you can add them later</span>
        </div>
        <Divider class="divider-gray-450 dark:divider-gray-750 mt-s w-full"></Divider>
      </div>

      <div class="mb-[65px] flex justify-center">
        <div class="flex w-[240px]">
          <Button :on-click="purchaseStore.buy" class="button-primary dark:button-colored h-[48px]">
            <span class="button__text">Pay</span>
          </Button>
        </div>
      </div>

      <div class="mb-m flex justify-center">
        <button class="text-caption-s text-gray-500 dark:text-gray-650" @click="() => purchaseStore.goPrev()">
          <span>← </span>
          <span class="underline underline-offset-2">Back</span>
        </button>
      </div>
    </div>
  </div>
</template>
